<script setup lang="ts">
  import { onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BadgeQC from '@qicgroupdoha/qic-connect-ds/components/BadgeQC'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import bedImage from './assets/bed.png'
  import flowersImage from './assets/flowers.png'
  import qrImage from './assets/qr.svg'

  interface Props {
    policyId: Nullable<string>
  }

  const props = defineProps<Props>()

  const HOTEL_BANNER_URL =
    'https://v536.adj.st/hotels/home?adj_t=23jis9ur&adj_redirect_android=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dapp.qd.qatardrive%26listing%3Dtravel&adj_redirect_ios=https%3A%2F%2Fapps.apple.com%2Fus%2Fapp%2Fqic-insurance-market-travel%2Fid1620043397%3Fppid%3D8e3b8907-241e-4c3b-81eb-1afd670dd26a'

  const { locale } = useI18n()
  const gaService = useGoogleAnalytics()

  onMounted(() => {
    gaService.sendViewBanner(props.policyId, 'hotel_banner')
  })

  const onButtonClick = () => {
    gaService.sendClickTravelKitBanner(props.policyId, 'choose_hotel', 'hotel_banner')
  }
</script>

<template>
  <div class="hotel-banner" data-test="hotel-banner">
    <BadgeQC variant="brand" filled class="hotel-banner__badge">
      {{ $t('visitors.success_page.hotel_block.badge') }}
    </BadgeQC>

    <img :src="flowersImage" alt="flowers" class="hotel-banner__flowers" />
    <img :src="bedImage" alt="bed" class="hotel-banner__bed" />

    <div class="hotel-banner__card">
      <img :src="qrImage" alt="qr_code" class="hotel-banner__qr" />

      <WrapperQC direction="column" gap="var(--spacing-5)">
        <WrapperQC direction="column" gap="var(--spacing-4)">
          <h3 class="hotel-banner__header heading-4">
            {{ $t('visitors.success_page.hotel_block.header') }}
          </h3>
          <p class="hotel-banner__desc text-regular">
            {{ $t('visitors.success_page.hotel_block.desc') }}
          </p>
        </WrapperQC>

        <div class="hotel-banner__button">
          <ButtonQC
            variant="primary"
            size="medium"
            :href="HOTEL_BANNER_URL"
            target="_blank"
            :icon-after-content="locale === 'en' ? 'arrow-narrow-right' : 'arrow-narrow-left'"
            data-test="hotel-banner-button"
            @click="onButtonClick"
          >
            {{ $t('visitors.success_page.hotel_block.button') }}
          </ButtonQC>
        </div>
      </WrapperQC>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .hotel-banner {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    min-height: 311px;
    padding: var(--spacing-5) var(--spacing-2-5) var(--spacing-2-5);
    border-radius: 28px;
    background: linear-gradient(162deg, #6f72ed 4.83%, #df7be8 66.1%, #ffb0b5 120.79%);

    &__badge {
      margin-inline-start: 16px;
    }

    &__flowers {
      position: absolute;
      top: 0;
      inset-inline-end: 44px;
      width: 84px;
      height: 151px;
    }

    &__bed {
      position: absolute;
      top: 23px;
      inset-inline-end: 47px;
      width: 221px;
      height: 150px;
    }

    &__card {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      gap: var(--spacing-5);
      padding: var(--spacing-7);
      border-radius: var(--radius-7);
      background: var(--colorBgSrfPrimary);
    }

    &__qr {
      flex-shrink: 0;
      width: 100px;
      height: 100px;
    }

    &__header,
    &__desc {
      color: var(--colorTxtSrfPrimary);
    }

    &__button {
      display: none;
    }
  }

  @media (max-width: 568px) {
    .hotel-banner {
      min-height: 390px;

      &__bed {
        top: 45px;
        inset-inline-end: 10px;
        width: 216px;
        height: 150px;
      }

      &__qr {
        display: none;
      }

      &__button {
        display: block;
      }
    }
  }
</style>
